import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IconEnum } from '@investoro/core';
import styled from 'styled-components';
import { AnalyticPanelContext } from 'utils/context/AnalyticPanelContext';
import { stringToNumberOrZero } from 'utils/formatters/stringToNumberOrZero';
import { AnalyticPanelCalculation, AnalyticPanelFilter, AnalyticPanelFlip, AnalyticPanelState } from 'utils/types/AnalyticPanelModule';

import CostCardsGrid from 'components/common/CostCardsGrid/CostCardsGrid';
import CostsSummary from 'components/common/CostCardsGrid/CostsSummary';
import { TimelineCostsGrid, TimelineStage } from 'components/common/CostCardsGrid/TimelineCostsGrid';
import { CostCardField, CostSection } from 'components/common/CostCardsGrid/types';
import InputContainer from 'components/common/InputContainer';
import AutocompleteInput from 'components/common/Inputs/AutocompleteInput/AutocompleteInput';
import TextInput from 'components/common/Inputs/TextInput';
import { activeNotification } from 'components/functions/activeNotification';

const PageWrapper = styled.div``;

const SaveFilterWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    background-color: var(--color-white);
    border-radius: var(--box-border-radius);
    padding: 30px 20px;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const InputWrapper = styled.div`
    padding: 0 20px;
    width: 100%;
`;

const SectionWrapper = styled.div<{ isSticky?: boolean }>`
    margin: 0px 120px;
    padding: 30px;
    bottom: 0;
    background-color: var(--color-alt-second);
    border-radius: var(--box-border-radius);

    ${(props) => props.isSticky && `
        position: sticky;
        padding: 0 30px 20px 30px;
        background-color: transparent;

        @media (max-width: 1100px) {
            bottom: calc(72px + env(safe-area-inset-bottom, 0px));
        }
    `}

    @media (max-width: 1200px) {
        margin: 20px 60px;
    }

    @media (max-width: 992px) {
        margin: 15px 30px;
        padding: 20px;
        ${(props) => props.isSticky && 'padding: 0 0 15px 0;'}
    }

    @media (max-width: 768px) {
        margin: 10px 20px;
        padding: 15px 10px;
        ${(props) => props.isSticky && 'padding: 0 0 10px 0;'}
    }
`;

const FiltersAnalyticPanel = () => {
    const {
        filtersAnalyticPanelState,
        setShowReport,
        handleChange,
        handleFilterChange,
        handleCalculationChange,
        handleFlipChange,
        recalculateOnBlur,
        recalculateWithCalculation,
        recalculateWithFlipStage,
        updateTotalField,
        updateCalculationAmountField,
        handleSetFilterValues,
        handleDeleteFilter,
        saveFilter
    } = useContext(AnalyticPanelContext);

    const [saveName, setSaveName] = useState('');

    const { filter, calculation, flip, costs, savedFilters, filterId, filterName } = filtersAnalyticPanelState;

    useEffect(() => {
        setSaveName(filterName || '');
    }, [filterId, filterName]);

    const savedCalculationsOptions = useMemo(() => {
        return savedFilters.map((savedFilter) => ({
            label: savedFilter.name,
            value: savedFilter.id,
            trash: true
        }));
    }, [savedFilters]);

    const salePriceError = (() => {
        const salePrice = stringToNumberOrZero(filtersAnalyticPanelState.salePrice);
        const purchasePrice = stringToNumberOrZero(filtersAnalyticPanelState.purchasePrice);

        return salePrice > 0 && purchasePrice > 0 && salePrice < purchasePrice;
    })();

    const investmentDataFields: CostCardField[] = [
        {
            title: 'Cena zakupu',
            fieldName: 'purchasePrice',
            value: filtersAnalyticPanelState.purchasePrice,
            suffix: 'zł',
            required: true
        },
        {
            title: 'Cena sprzedaży',
            fieldName: 'salePrice',
            value: filtersAnalyticPanelState.salePrice,
            suffix: 'zł',
            required: true,
            isError: salePriceError,
            errorMessage: 'Cena sprzedaży nie może być niższa od ceny zakupu'
        },
        {
            title: 'Powierzchnia',
            fieldName: 'area',
            value: filtersAnalyticPanelState.area,
            suffix: 'm²',
            required: true
        },
        {
            title: 'Data zakupu',
            fieldName: 'purchaseDate',
            value: filtersAnalyticPanelState.purchaseDate,
            suffix: '',
            type: 'date'
        },
        {
            title: 'Data sprzedaży',
            fieldName: 'saleDate',
            value: filtersAnalyticPanelState.saleDate,
            suffix: '',
            type: 'date'
        },
        {
            title: 'Czas inwestycji',
            fieldName: 'investmentTime',
            value: filtersAnalyticPanelState.investmentTime,
            suffix: 'mies.',
            required: true
        },
        {
            title: 'Adres',
            fieldName: 'address',
            value: filtersAnalyticPanelState.address || '',
            suffix: '',
            type: 'string',
            placeholder: 'Wpisz adres inwestycji'
        },
        {
            title: 'Piętro',
            fieldName: 'floor',
            value: filtersAnalyticPanelState.floor,
            suffix: ''
        },
        {
            title: 'Liczba pokoi',
            fieldName: 'rooms',
            value: filtersAnalyticPanelState.rooms,
            suffix: ''
        }
    ];

    const costFields: CostCardField[] = [
        {
            title: 'Remont z materiałami (za m²)',
            fieldName: 'renovation',
            section: CostSection.RENOVATION,
            value: filter?.renovation,
            totalValue: costs?.renovation,
            suffix: 'zł/m²',
            totalValueSuffix: 'zł',
            showTotalValue: true,
            valueLabel: 'Stawka',
            totalLabel: 'Łącznie',
            isFilterField: true
        },
        {
            title: 'Notariusz',
            fieldName: 'notary',
            section: CostSection.PURCHASE_COSTS,
            value: filter?.notary,
            suffix: 'zł',
            valueLabel: 'Wartość',
            isFilterField: true
        },
        {
            title: 'PCC',
            fieldName: 'pcc',
            section: CostSection.PURCHASE_COSTS,
            value: filter?.pcc,
            totalValue: costs?.pcc,
            suffix: '%',
            totalValueSuffix: 'zł',
            showTotalValue: true,
            valueLabel: 'Procent',
            totalLabel: 'Łącznie',
            isFilterField: true
        },
        {
            title: 'Pośrednik przy zakupie',
            fieldName: 'brokerPercent',
            section: CostSection.PURCHASE_COSTS,
            value: filter?.brokerPercent,
            totalValue: costs?.brokerPercent,
            suffix: '%',
            totalValueSuffix: 'zł',
            showTotalValue: true,
            valueLabel: 'Procent',
            totalLabel: 'Łącznie',
            isFilterField: true
        },
        {
            title: 'Media (miesięcznie)',
            fieldName: 'maintenanceFees',
            section: CostSection.OPERATING_COSTS,
            value: filter?.maintenanceFees,
            totalValue: costs?.maintenanceFees,
            suffix: 'zł',
            totalValueSuffix: 'zł',
            showTotalValue: true,
            valueLabel: 'Miesięcznie',
            totalLabel: 'Łącznie',
            isFilterField: true
        },
        {
            title: 'Opłaty eksploatacyjne (za m²)',
            fieldName: 'rentFees',
            section: CostSection.OPERATING_COSTS,
            value: filter?.rentFees,
            totalValue: costs?.rentFees,
            suffix: 'zł/m²',
            totalValueSuffix: 'zł',
            showTotalValue: true,
            valueLabel: 'Stawka',
            totalLabel: 'Łącznie',
            isFilterField: true
        },
        {
            title: 'Fotograf',
            fieldName: 'photographerAmount',
            section: CostSection.SALE_COSTS,
            value: filter?.photographerAmount,
            suffix: 'zł',
            valueLabel: 'Wartość',
            isFilterField: true
        },
        {
            title: 'Ogłoszenia Portale',
            fieldName: 'portalFees',
            section: CostSection.SALE_COSTS,
            value: filter?.portalFees,
            suffix: 'zł',
            valueLabel: 'Wartość',
            isFilterField: true
        },
        {
            title: 'Meble ruchome, wyposażenie',
            fieldName: 'equipment',
            section: CostSection.SALE_COSTS,
            value: filter?.equipment,
            suffix: 'zł',
            valueLabel: 'Wartość',
            isFilterField: true
        },
        {
            title: 'Home staging',
            fieldName: 'homeStaging',
            section: CostSection.SALE_COSTS,
            value: filter?.homeStaging,
            suffix: 'zł',
            valueLabel: 'Wartość',
            isFilterField: true
        },
        {
            title: 'Pośrednik przy sprzedaży',
            fieldName: 'saleBrokerPercent',
            section: CostSection.SALE_COSTS,
            value: filter?.saleBrokerPercent,
            totalValue: costs?.saleBrokerPercent,
            suffix: '%',
            totalValueSuffix: 'zł',
            showTotalValue: true,
            valueLabel: 'Procent',
            totalLabel: 'Łącznie',
            isFilterField: true
        },
        {
            title: 'Inne koszty sprzedaży',
            fieldName: 'otherSaleCosts',
            section: CostSection.SALE_COSTS,
            value: filter?.otherSaleCosts,
            suffix: 'zł',
            valueLabel: 'Wartość',
            isFilterField: true
        },
        {
            title: 'Koszty finansowania (odsetki)',
            fieldName: 'saleInterest',
            section: CostSection.FINANCING_COSTS,
            value: filter?.saleInterest,
            suffix: 'zł',
            valueLabel: 'Wartość',
            isFilterField: true
        },
        {
            title: 'Pozostałe koszty',
            fieldName: 'otherCosts',
            section: CostSection.OTHER_COSTS,
            value: filter?.otherCosts,
            suffix: 'zł',
            valueLabel: 'Wartość',
            isFilterField: true
        }
    ];

    const timelineStages: TimelineStage[] = [
        {
            id: 'stage1',
            label: '1 etap - Zakup',
            description: 'Cena zakupu mieszkania',
            fieldName: 'firstStage',
            value: stringToNumberOrZero(flip?.firstStage)
        },
        {
            id: 'stage2',
            label: '2 etap - Remont i zakup',
            description: 'Remont + koszty okołozakupowe',
            fieldName: 'secondStage',
            value: stringToNumberOrZero(flip?.secondStage)
        },
        {
            id: 'stage3',
            label: '3 etap - Eksploatacja',
            description: 'Koszty eksploatacyjne',
            fieldName: 'thirdStage',
            value: stringToNumberOrZero(flip?.thirdStage)
        },
        {
            id: 'stageFinal',
            label: 'Koszty końcowe',
            description: 'Sprzedaż + finansowanie + pozostałe',
            fieldName: 'finalCosts',
            value: stringToNumberOrZero(flip?.finalCosts),
            allowNegative: true
        }
    ];

    const capitalProfitFields: CostCardField[] = [
        {
            title: 'Kapitał Flippera',
            fieldName: 'fliperCapitalPercent',
            value: calculation?.flipperCapitalPercent,
            totalValue: calculation?.flipperCapitalAmount,
            suffix: '%',
            totalValueSuffix: 'zł',
            showTotalValue: true
        },
        {
            title: 'Kapitał Investora',
            fieldName: 'investorCapitalPercent',
            value: calculation?.investorCapitalPercent,
            totalValue: calculation?.investorCapitalAmount,
            suffix: '%',
            totalValueSuffix: 'zł',
            showTotalValue: true
        },
        {
            title: 'Zysk Flippera',
            fieldName: 'fliperProfitPercent',
            value: calculation?.flipperProfitPercent,
            totalValue: calculation?.flipperProfitAmount,
            suffix: '%',
            totalValueSuffix: 'zł',
            showTotalValue: true
        },
        {
            title: 'Zysk Investora',
            fieldName: 'investorProfitPercent',
            value: calculation?.investorProfitPercent,
            totalValue: calculation?.investorProfitAmount,
            suffix: '%',
            totalValueSuffix: 'zł',
            showTotalValue: true
        }
    ];

    const totalCosts = stringToNumberOrZero(costs?.sumOfCosts);
    const projectBudget = stringToNumberOrZero(calculation?.projectCapital);

    const handleValueChange = (fieldName: string, value: string | number) => {
        handleChange(fieldName as keyof AnalyticPanelState, value as string | number | null);
    };

    const handleCostFieldChange = (fieldName: string, value: string | number) => {
        const strValue = typeof value === 'number' ? value.toString() : value;
        handleFilterChange(fieldName as keyof AnalyticPanelFilter, strValue);
    };

    const handleCalculationFieldChange = (fieldName: string, value: string | number) => {
        let strValue = typeof value === 'number' ? value.toString() : value;

        const percentageFields = [
            'fliperCapitalPercent',
            'investorCapitalPercent',
            'fliperProfitPercent',
            'investorProfitPercent'
        ];

        if (percentageFields.includes(fieldName)) {
            const numValue = parseFloat(strValue) || 0;

            if (numValue > 100) {
                strValue = '100';
            }
        }

        handleCalculationChange(fieldName as keyof AnalyticPanelCalculation, strValue);
    };

    const handleFlipFieldChange = (fieldName: string, value: string | number) => {
        const strValue = typeof value === 'number' ? value.toString() : value;
        handleFlipChange(fieldName as keyof AnalyticPanelFlip, strValue);
    };

    const handleFlipFieldBlur = (fieldName: string, value: string | number) => {
        const strValue = typeof value === 'number' ? value.toString() : value;
        recalculateWithFlipStage(fieldName as keyof AnalyticPanelFlip, strValue);
    };

    const handleFieldBlur = useCallback(() => {
        recalculateOnBlur();
    }, [recalculateOnBlur]);

    const handleCapitalProfitBlur = (fieldName: string, value: string | number) => {
        const strValue = typeof value === 'number' ? value.toString() : value;

        const percentagePairs: Record<string, string> = {
            flipperCapitalPercent: 'investorCapitalPercent',
            investorCapitalPercent: 'flipperCapitalPercent',
            flipperProfitPercent: 'investorProfitPercent',
            investorProfitPercent: 'flipperProfitPercent'
        };

        const pairedField = percentagePairs[fieldName];

        if (pairedField) {
            let numValue = parseFloat(strValue) || 0;
            numValue = Math.min(100, Math.max(0, numValue));
            const complementValue = 100 - numValue;

            recalculateWithCalculation({
                [fieldName]: numValue.toString(),
                [pairedField]: complementValue.toString()
            });
        } else {
            recalculateOnBlur();
        }
    };

    const handleCostTotalBlur = (fieldName: string, value: string | number) => {
        const strValue = typeof value === 'number' ? value.toString() : value;
        updateTotalField(fieldName as keyof AnalyticPanelFilter, strValue);
    };

    const handleCapitalProfitAmountBlur = (fieldName: string, value: string | number) => {
        const strValue = typeof value === 'number' ? value.toString() : value;
        updateCalculationAmountField(fieldName as keyof AnalyticPanelCalculation, strValue);
    };

    const handleGenerateReport = () => {
        const price = stringToNumberOrZero(filtersAnalyticPanelState.purchasePrice);
        const areaVal = stringToNumberOrZero(filtersAnalyticPanelState.area);

        if (price <= 0 && areaVal <= 0) {
            activeNotification('Brak wymaganych danych', 'Wypełnij pola "Cena zakupu" oraz "Powierzchnia"', 'warning');

            return;
        }

        if (price <= 0) {
            activeNotification('Brak ceny zakupu', 'Wypełnij pole "Cena zakupu"', 'warning');

            return;
        }

        if (areaVal <= 0) {
            activeNotification('Brak metrażu', 'Wypełnij pole "Powierzchnia"', 'warning');

            return;
        }

        setShowReport(true);
        document.body.scrollTo(0, 0);
    };

    const handleSaveCalculation = async () => {
        if (!saveName.trim()) {
            activeNotification('Brak nazwy', 'Wpisz nazwę kalkulacji przed zapisaniem', 'warning');

            return;
        }

        await saveFilter(saveName);

        if (!filterId) {
            setSaveName('');
        }
    };

    const handleSelectCalculation = async (filterId?: string | number) => {
        const id = typeof filterId === 'string' ? parseInt(filterId, 10) : filterId;

        if (id && id > 0 && !isNaN(id)) {
            await handleSetFilterValues(id);
        }
    };

    const handleDeleteCalculation = (filterId: string | number) => {
        const id = typeof filterId === 'string' ? parseInt(filterId, 10) : filterId;
        if (!Number.isFinite(id)) return;
        handleDeleteFilter(id);
    };

    return (
        <PageWrapper>
            <SectionWrapper>
                <CostCardsGrid
                    fields={investmentDataFields}
                    title="Dane inwestycji"
                    showLegend={false}
                    showItemCount={false}
                    onValueChange={handleValueChange}
                    onValueBlur={handleFieldBlur}
                />
            </SectionWrapper>

            <SectionWrapper>
                <CostCardsGrid
                    fields={costFields}
                    title="Szczegóły kosztów"
                    onValueChange={handleCostFieldChange}
                    onValueBlur={handleFieldBlur}
                    onTotalValueBlur={handleCostTotalBlur}
                />
            </SectionWrapper>

            <SectionWrapper>
                <CostCardsGrid
                    fields={capitalProfitFields}
                    title="Podział kapitału i zysku"
                    showLegend={false}
                    showItemCount={false}
                    onValueChange={handleCalculationFieldChange}
                    onValueBlur={handleCapitalProfitBlur}
                    onTotalValueBlur={handleCapitalProfitAmountBlur}
                />
            </SectionWrapper>

            <SectionWrapper>
                <TimelineCostsGrid
                    stages={timelineStages}
                    title="Koszty flipa w czasie / Zapotrzebowanie na kapitał"
                    totalValue={costs?.sumOfCosts}
                    readOnly
                    onValueChange={handleFlipFieldChange}
                    onValueBlur={handleFlipFieldBlur}
                />
            </SectionWrapper>

            <SectionWrapper>
                <SaveFilterWrapper>
                    <InputWrapper>
                        <InputContainer title="Wybierz kalkulację" icon={IconEnum.CALCULATOR}>
                            <AutocompleteInput
                                noTyping
                                readOnly
                                optionsIcon={IconEnum.BOOKMARK}
                                value={filterId}
                                onChange={handleSelectCalculation}
                                handleDeleteFilter={handleDeleteCalculation}
                                placeholder="Wybierz kalkulację..."
                                options={savedCalculationsOptions}
                            />
                        </InputContainer>
                    </InputWrapper>
                    <InputWrapper>
                        <InputContainer title="Zapisz kalkulację" icon={IconEnum.BOOKMARK}>
                            <TextInput
                                value={saveName}
                                onChange={(e) => setSaveName(e.target.value)}
                                placeholder="Wpisz nazwę kalkulacji..."
                                onAction={handleSaveCalculation}
                                actionIcon={IconEnum.ARROW_RIGHT}
                                icon={IconEnum.BOOKMARK}
                                outline
                                variant="warning"
                                isSaveFilterButton
                            />
                        </InputContainer>
                    </InputWrapper>
                </SaveFilterWrapper>
            </SectionWrapper>

            <SectionWrapper isSticky>
                <CostsSummary
                    totalCosts={totalCosts}
                    projectBudget={projectBudget}
                    estimatedProfitNetto={calculation ? stringToNumberOrZero(calculation.estimatedProfitNetto) : null}
                    roi={calculation ? stringToNumberOrZero(calculation.roi) : null}
                    margin={calculation ? stringToNumberOrZero(calculation.margin) : null}
                    onGenerateReport={handleGenerateReport}
                />
            </SectionWrapper>

        </PageWrapper>
    );
};

export default FiltersAnalyticPanel;
